/* ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THIS FILE IS A COPY. THE ORIGINAL LIVES IN THE WEB REPO:
 *     HypeTunes_Web_2026/src/components/three/faceMount.js
 *
 * Khari, 2026-08-31: *"Why would we need any new copies when these faces should
 * already be attached to the models?"* — the right question, and the answer is
 * that a face is NOT attached to a model (an avatar GLB has zero morph targets
 * and no mouth mesh), and that this should not be a third implementation. So it
 * is one file, copied once, and the copy is enforced.
 *
 * ⚠ DO NOT EDIT BELOW THE SENTINEL. Fix the Web original, then:
 *     node scripts/check_face_decal_sync.mjs --sync
 * ───────────────────────────────────────────────────────────────────────────── */

/* ── VERBATIM FROM HypeTunes_Web_2026 BELOW THIS LINE ── */
import { buildFaceDecal, attachFaceDecal, debugDecalMaterial, videoDecalMaterial } from './faceDecal';

/**
 * MOUNT A SPEAKING FACE ON A BODY — the World's side of the mouth loop.
 *
 * Khari, 2026-08-31: *"turn on the video faces, and make sure they animate when
 * the avatar is chatting/talking."*
 *
 * ⚠ EXTRACTED FROM `ModelViewer3D.jsx` (`buildMouth` / `applyFace` /
 * `stepFace`), and it is the ONLY implementation — the World runs a guarded
 * verbatim copy of this file, checked by `World/scripts/check_face_decal_sync.mjs`.
 * A face is not part of a body: an avatar GLB has ZERO morph targets and no
 * mouth mesh. It is an mp4, eleven numbers, and this — which cuts a patch out of
 * the body's own triangles and projects the clip onto it.
 *
 * ⚠ `ModelViewer3D` STILL HAS ITS OWN INLINE COPY of this logic. It predates the
 * extraction and rewiring it means surgery on the component currently driving
 * the channel and the Lab. It should adopt this file; until it does, a change
 * here must be made there too.
 *
 * ⚠ THE DECAL IS PERMANENT; ONLY THE CLIP STARTS AND STOPS. Paused, it holds
 * frame 0, which is the plate the body was built from — the artist's own resting
 * face — so nothing swaps when they start talking. `speaking` means MOVING.
 */

const FACE_FADE_IN = 0.26;
// Linear progress eased at both ends — a straight ramp on opacity has a visible
// corner where it starts and where it stops.
const smoothT = (t) => t * t * (3 - 2 * t);

/* ── HOW MANY MOUTHS A ROOM MAY DECODE AT ONCE ─────────────────────────────
 * ⚠ THIS IS THE ONE THING THE WEB VIEWER NEVER HAD TO THINK ABOUT. It draws a
 * single body; a district draws a dozen, and every face is a real `<video>`
 * holding a real decoder. Desktop shrugs. Mobile Safari does not — the limit
 * has historically been as low as one, and when it bites the failure is not an
 * error, it is videos that never produce a frame, which renders as a patch of
 * flat colour across a face. The World is used on phones (that is why dev runs
 * over HTTPS at all), so this is a live risk, not a theoretical one.
 *
 * So: a budget. The body you are TALKING TO always gets one — it is the only
 * one whose mouth actually moves — and everyone else is served in mount order
 * until the budget runs out. A body refused a decal keeps the face it was built
 * with, which is exactly how it looks today. `?faces=N` tunes it, `?faces=0`
 * turns the feature off entirely for an A/B.
 *
 * ⚠ AND IT SAYS SO WHEN IT BITES. A silently faceless resident is
 * indistinguishable from a body whose loop was never generated. */
const qp = (k, d) => {
  const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
  return Number.isFinite(v) ? v : d;
};
/* ⚠ THE DEFAULT IS SPLIT BY DEVICE, because the constraint is. `pointer: coarse`
   is true on phones and tablets and false on a desktop — the standard test for
   the touch class, and it happens to be the exact axis the decoder limit falls
   along, without sniffing a user-agent string. A desktop can comfortably hold
   the whole street (9 face-capable bodies on the roster today, and the number
   only grows); a phone cannot, and being wrong there costs a face-shaped patch
   of flat colour rather than a dropped frame. `?faces=N` overrides either. */
const TOUCH = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: coarse)').matches;
const FACE_BUDGET = qp('faces', TOUCH ? 4 : 16);
let spent = 0;
let warned = false;

/**
 * @param {THREE.Object3D} model  a cloned body already in the scene
 * @param {object} cfg            `facePanelFor(pose)` — the box plus `src`
 * @param {{priority?: boolean, label?: string}} opts  priority = this is the body
 *        being spoken to, and it bypasses the budget.
 * @returns {{step, setSpeaking, dispose, info}|null}
 */
export function mountFaceDecal(model, cfg, { priority = false, label = 'body' } = {}) {
  if (!model || !cfg?.src) return null;
  if (!priority && spent >= FACE_BUDGET) {
    if (!warned) {
      warned = true;
      console.warn(`[face] ${label}: no video face — the room is already decoding ${spent} of them`
        + ` (limit ${FACE_BUDGET}). The body keeps its built-in face. Tune with ?faces=N.`);
    }
    return null;
  }

  let skinned = null;
  model.traverse((o) => { if (!skinned && o.isSkinnedMesh) skinned = o; });
  if (!skinned) return null;

  const cut = buildFaceDecal(skinned, cfg);
  if (!cut) {
    console.warn(`[face] ${label}: nothing inside the face box — the decal was not cut.`);
    return null;
  }

  let video = null;
  let material;
  /* Declared before the video, because the `seeked` listener closes over it —
     it only ever FIRES after the assignment below, but a `const` used above its
     own declaration is a trap waiting for someone to move one line. */
  let decal = null;
  if (cfg.src && !cfg.debug) {
    video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.loop = true;
    video.muted = true;            // ⚠ or autoplay is refused outright
    video.playsInline = true;
    /* ⚠ `auto`, BECAUSE IT SPENDS ITS LIFE PAUSED. The default fetches only
       metadata, and a video paused before it has ever decoded a frame never
       produces one — the decal would render an empty texture over the face and
       stay that way until the body spoke. */
    video.preload = 'auto';
    /* ⚠ A BAD PATH MUST NOT FAIL SILENTLY, and it very nearly always does: a
       dev server answers an unknown path with the SPA shell — HTTP 200,
       text/html — so the request looks perfect and decoding just fails. */
    video.addEventListener('error', () => {
      console.warn(`[face] ${label}: video failed to load: ${cfg.src}`
        + ' — check the path (a dev server answers an unknown path with the app shell, not a 404)');
    });
    /* ⚠ A SEEK MUST BE PUSHED TO THE GPU BY HAND. A paused video produces no
       new frames and `VideoTexture` only uploads when one arrives, so moving to
       the resting frame lands on the element and never reaches the texture —
       the body would hold whatever half-open mouth it stopped on. `seeked` is
       the moment the new frame actually exists. */
    video.addEventListener('seeked', () => {
      if (decal?.material?.map) decal.material.map.needsUpdate = true;
    });
    video.src = cfg.src;
    video.play().catch(() => { /* it plays when it can */ });
    material = videoDecalMaterial(video, {
      feather: cfg.feather,
      shape: cfg.shape || 'oval',
      /* The mask has to know the fit, or the oval scales with the clip — and
         only the cutter can work it out, because the clip is measured in a fixed
         reference frame while the oval belongs to the patch. */
      fit: cut.fit,
      oval: cfg.oval,
      ovalY: cfg.ovalY,
      featherTop: cfg.featherTop,
      maskTop: cfg.maskTop,
      maskTopFade: cfg.maskTopFade,
      key: cfg.key || null,
      keyTolerance: cfg.keyTolerance,
      keySoftness: cfg.keySoftness,
    });
  } else {
    material = debugDecalMaterial();
  }

  decal = attachFaceDecal(skinned, cut.geometry, material);
  spent += 1;

  const baseOpacity = material.opacity;
  const rest = Number.isFinite(cfg.rest) ? cfg.rest : 0;
  let fadeIn = 0;
  let speaking = cfg.speaking !== false;
  let disposed = false;
  /* ── OFFLINE CAPTURE ──────────────────────────────────────────────────────
     ⚠ A `<video>` PLAYS ON WALL CLOCK AND NOTHING ELSE CAN STEP IT. When the
     stage is being captured frame by frame the scene clock is driven by hand,
     but this element would carry on decoding in real time — so a take that
     costs 30s of wall clock to render 15s of scene would show 30s of face. The
     first capture came back with the mouth running fast for exactly this
     reason, one bug after the mixers did the same thing for a different one.
     Under capture the clip is PAUSED and seeked per frame instead. */
  let capture = false;
  let clipT = 0;

  // A video with no decoded frame yet. Until it has one there is nothing to fade
  // in and nothing to freeze — pausing here is what strands it empty.
  const ready = () => !video || video.readyState >= 2;
  const restFrame = () => {
    if (!video || !decal) return;
    if (Math.abs(video.currentTime - rest) < 0.01) return;
    video.currentTime = rest;
  };
  const applyFace = () => {
    if (disposed) return;
    const a = smoothT(fadeIn);
    decal.material.opacity = baseOpacity * a;
    decal.visible = a > 0.001;
    if (!video) return;
    /* Capture owns the clip's time; `captureStep` moves it. Playing here would
       hand it back to the wall clock the moment anybody started speaking. */
    if (capture) { if (!video.paused) video.pause(); return; }
    /* ⚠ THE CLIP RUNS ONLY WHILE THEY TALK. A `<video>` feeding a texture
       decodes every frame whether or not the mouth is moving, and a resident is
       silent for nearly all of its life. Paused it costs nothing and still shows
       a face, which is the whole point of holding the first frame. */
    if (speaking) {
      if (video.paused) video.play().catch(() => { /* it plays when it can */ });
    } else if (!video.paused && ready()) {
      video.pause();
      restFrame();
    }
  };

  applyFace();

  return {
    info: { triangles: cut.triangles, vertices: cut.vertices },
    /** One frame of the arrival fade. Cheap and self-cancelling once done. */
    step: (dt) => {
      if (disposed || fadeIn >= 1) return;
      /* ⚠ HOLD THE FADE UNTIL THERE IS A PICTURE. Starting it on an undecoded
         video fades a BLACK patch onto the face, which is worse than the pop it
         was added to avoid. */
      if (!ready()) return;
      if (!speaking && !video?.paused) applyFace();
      fadeIn = Math.min(1, fadeIn + dt / FACE_FADE_IN);
      applyFace();
    },
    /**
     * Turn hand-driven time on or off. On: the clip stops playing and only
     * moves when `captureStep` says so. Off: normal wall-clock behaviour
     * resumes exactly as before.
     */
    setCapture: (on) => {
      capture = !!on;
      if (!video) return;
      if (capture) { video.pause(); clipT = video.currentTime || 0; }
      applyFace();
    },
    /**
     * Advance the clip by `dt` SECONDS of scene time and wait for the picture.
     *
     * ⚠ IT AWAITS `seeked`, AND THAT IS THE WHOLE POINT. A seek is async and a
     * paused video produces no frames, so `VideoTexture` only uploads when one
     * actually arrives — returning early would render the PREVIOUS face onto
     * the current frame, every frame, which reads as a lip-sync offset rather
     * than as a capture bug. The `seeked` listener above sets `needsUpdate`.
     *
     * ⚠ ONLY WHILE SPEAKING, because that is what the live behaviour is: a
     * silent body holds its resting frame. Reproducing it here rather than
     * running the loop continuously is what keeps a captured take looking like
     * the stage it came from.
     */
    captureStep: (dt) => {
      if (disposed || !video || !capture) return Promise.resolve();
      if (!video.paused) video.pause();
      if (!ready()) return Promise.resolve();
      let target;
      if (speaking) {
        const dur = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
        clipT += dt;
        /* ⚠ NEVER SEEK TO THE VERY END. Landing on (or past) the last frame
           returns nothing on most encodes and the texture keeps what it had —
           the same lesson the keyframe sampler learned server-side. */
        target = dur ? Math.min(clipT % dur, dur - 0.02) : clipT;
      } else {
        target = rest;
      }
      if (Math.abs(video.currentTime - target) < 1e-4) return Promise.resolve();
      return new Promise((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          video.removeEventListener('seeked', finish);
          resolve();
        };
        video.addEventListener('seeked', finish);
        video.currentTime = target;
        /* A decode that never lands must not hang a 360-frame capture. The
           frame is then simply the one before it — a visible stutter, not a
           stalled run with nothing on screen to say why. */
        setTimeout(finish, 250);
      });
    },
    setSpeaking: (v) => {
      const next = !!v;
      if (next === speaking) return;
      speaking = next;
      applyFace();
    },
    dispose: () => {
      if (disposed) return;
      disposed = true;
      spent = Math.max(0, spent - 1);
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
      decal.parent?.remove(decal);
      decal.geometry.dispose();
      decal.material.map?.dispose?.();
      decal.material.dispose();
    },
  };
}

export default mountFaceDecal;
